/**
 * The reader's time zone, persisted.
 *
 * This is the fifth module allowed to touch `localStorage`. It holds one
 * IANA name and nothing else. An unset key means UTC, so a
 * signed-out load still persists only the theme key until the reader chooses.
 *
 * A storage failure leaves the console working. The choice applies to this
 * tab and does not survive the next reload.
 */

import { parseTimeZone } from './time-zone.ts'

/** The only key this module writes. */
export const TIME_ZONE_KEY = 'ui-internal.timeZone'

/**
 * Read the held zone.
 *
 * @returns The zone, or null when there is none or the stored text is not one
 *   this module writes.
 */
export function readTimeZone(): string | null {
  try {
    return parseTimeZone(localStorage.getItem(TIME_ZONE_KEY))
  } catch {
    return null
  }
}

/**
 * Hold a zone across reloads.
 *
 * @param timeZone - The zone the reader chose. A name this module does not
 *   write is ignored.
 */
export function writeTimeZone(timeZone: string): void {
  if (parseTimeZone(timeZone) === null) {
    return
  }
  try {
    localStorage.setItem(TIME_ZONE_KEY, timeZone)
  } catch {
    // Quota, or storage disabled. The choice applies to this tab and simply
    // does not survive the next reload.
  }
}
